'use client';

import { useEffect, useRef, useState } from 'react';
import { useAuth } from '@/lib/context/AuthContext';
import { updateStatus } from '@/lib/api/dashboard';
import type { DashboardUser } from '@/lib/types/dashboard';

type Status = DashboardUser['status'];

const STATUS_META: Record<Status, { label: string; color: string }> = {
  available: { label: 'Online', color: 'var(--mx-success)' },
  busy: { label: 'Sibuk', color: 'var(--mx-danger)' },
  dnd: { label: 'Jangan diganggu', color: 'var(--mx-danger)' },
  away: { label: 'Away', color: 'var(--mx-warning)' },
  out_of_office: { label: 'Di luar kantor', color: 'var(--mx-slate-400)' },
};
const ORDER: Status[] = ['available', 'busy', 'dnd', 'away', 'out_of_office'];

function Dot({ color }: { color: string }) {
  return <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: '50%', background: color, flex: 'none' }} />;
}

export function StatusPill() {
  const { user } = useAuth();
  const [status, setStatus] = useState<Status>(user?.status || 'available');
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Sinkron dari profil saat user termuat.
  useEffect(() => {
    if (user?.status) setStatus(user.status);
  }, [user?.status]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const meta = STATUS_META[status] || STATUS_META.available;

  const choose = (next: Status) => {
    setStatus(next); // optimistik
    setOpen(false);
    void updateStatus(next);
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        className="mx-statuspill"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Status: ${meta.label}. Ubah status`}
        onClick={() => setOpen((v) => !v)}
        style={{ border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}
      >
        <Dot color={meta.color} />
        {meta.label}
      </button>

      {open && (
        <div
          className="mx-menu"
          role="menu"
          aria-label="Ubah status"
          style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 60, minWidth: 200 }}
        >
          {ORDER.map((s) => (
            <button
              key={s}
              type="button"
              role="menuitemradio"
              aria-checked={s === status}
              className="mx-menu__item mx-menu__item--check"
              onClick={() => choose(s)}
            >
              <Dot color={STATUS_META[s].color} />
              {STATUS_META[s].label}
              <span className="mx-menu__check" aria-hidden="true">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 6 9 17l-5-5" />
                </svg>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
